import { create } from 'zustand'
import type { AuthenticatedUser, LoginResponse, StoreMembership } from '@iobiz/shared'
import { api, deviceId, setAccessToken, setSessionLostHandler } from '../api/client.js'

/**
 * Session de l'utilisateur.
 *
 * ⚠️ Le jeton d'accès n'est PAS conservé ici : il vit dans le module client,
 * en mémoire. Ce store ne détient que l'identité et les appartenances, qui ne
 * sont pas des secrets.
 */

/** Dernière boutique ouverte : évite de repasser par le sélecteur chaque matin. */
const LAST_STORE_KEY = 'iobiz.lastStore'

/**
 * Identité et appartenances mises en cache pour la reprise HORS LIGNE.
 *
 * ⚠️ Aucun secret ici — ni jeton d'accès, ni jeton de rafraîchissement, qui
 * restent respectivement en mémoire et dans un cookie httpOnly. Seulement de
 * quoi redessiner l'interface quand le serveur est injoignable : sans ce
 * cache, rouvrir l'application installée sans réseau renverrait à l'écran de
 * connexion, alors que toutes les données sont là, dans IndexedDB.
 */
const SESSION_CACHE_KEY = 'iobiz.session'

interface SessionCache {
  user: AuthenticatedUser
  stores: StoreMembership[]
}

function lireCache(): SessionCache | null {
  try {
    const brut = localStorage.getItem(SESSION_CACHE_KEY)
    return brut ? (JSON.parse(brut) as SessionCache) : null
  } catch {
    return null
  }
}

function ecrireCache(cache: SessionCache | null): void {
  try {
    if (cache) localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cache))
    else localStorage.removeItem(SESSION_CACHE_KEY)
  } catch {
    // Quota plein ou navigation privée : la reprise hors ligne sera dégradée,
    // ce n'est pas une raison pour empêcher la connexion.
  }
}

interface SessionState {
  user: AuthenticatedUser | null
  stores: StoreMembership[]
  /** `true` tant que la restauration initiale n'a pas abouti ou échoué. */
  loading: boolean
  error: string | null

  /**
   * @param storeSlug boutique, pour un compte d'ÉQUIPE.
   *
   * ⭐ Présente, le mot de passe est vérifié dans le schéma de cette boutique.
   * Absente, on cherche dans la table commune — administrateurs de boutique et
   * de plateforme. Les deux populations ne vivent pas au même endroit.
   */
  login: (email: string, password: string, storeSlug?: string) => Promise<void>
  logout: () => Promise<void>
  restore: () => Promise<void>
  /**
   * Reprend le compte tel que le serveur vient de le renvoyer.
   *
   * ⚠️ À préférer à `restore()` après avoir modifié SON profil : `restore()`
   * repasse par `loading`, ce qui démonte la page en cours et emporte avec
   * elle le message de confirmation. L'utilisateur enregistrait sa photo et ne
   * voyait rien se passer.
   */
  appliquerProfil: (user: LoginResponse['user'], stores: StoreMembership[]) => void
  /**
   * Reprend les horaires d'une boutique après leur enregistrement.
   *
   * ⚠️ La session les EMBARQUE pour que la prise de rendez-vous fonctionne hors
   * ligne. Les modifier sans les reporter ici laisserait l'agenda proposer les
   * anciens créneaux jusqu'à la prochaine connexion — un rendez-vous pris un
   * jour désormais fermé.
   */
  majHorairesBoutique: (storeId: string, horaires: StoreMembership['openingHours']) => void
  storeBySlug: (slug: string) => StoreMembership | undefined
  rememberStore: (slug: string) => void
  lastStore: () => string | null
}

function applyLogin(set: (partial: Partial<SessionState>) => void, data: LoginResponse): void {
  setAccessToken(data.accessToken)
  ecrireCache({ user: data.user, stores: data.stores })
  set({ user: data.user, stores: data.stores, error: null, loading: false })
}

export const useSessionStore = create<SessionState>()((set, get) => ({
  user: null,
  stores: [],
  loading: true,
  error: null,

  async login(email, password, storeSlug) {
    set({ error: null })
    const data = await api.post<LoginResponse>('/auth/login', {
      email,
      password,
      ...(storeSlug ? { storeSlug } : {}),
      deviceId: deviceId(),
      deviceLabel: navigator.userAgent.slice(0, 100),
    })
    applyLogin(set, data)
  },

  majHorairesBoutique(storeId, horaires) {
    const stores = get().stores.map((b) =>
      b.storeId === storeId ? { ...b, openingHours: horaires } : b,
    )
    const user = get().user
    if (user) ecrireCache({ user, stores })
    set({ stores })
  },

  appliquerProfil(user, stores) {
    ecrireCache({ user, stores })
    set({ user, stores })
  },

  async logout() {
    try {
      await api.post('/auth/logout')
    } finally {
      // La session locale est effacée même si l'appel échoue : l'utilisateur a
      // demandé à se déconnecter, l'interface doit obéir immédiatement.
      setAccessToken(null)
      localStorage.removeItem(LAST_STORE_KEY)
      ecrireCache(null)
      set({ user: null, stores: [], loading: false })
    }
  },

  /**
   * Rejoue le cookie httpOnly au démarrage : un rechargement de page ne doit
   * pas obliger à ressaisir son mot de passe, alors que le jeton d'accès,
   * gardé en mémoire, a disparu.
   */
  async restore() {
    set({ loading: true })

    // Hors ligne, inutile d'attendre un aller-retour voué à l'échec : on
    // repart directement du cache. L'utilisateur retrouve sa réplique, et le
    // moteur reprendra la synchronisation au retour du réseau.
    if (!navigator.onLine) {
      const cache = lireCache()
      set({ user: cache?.user ?? null, stores: cache?.stores ?? [], loading: false })
      return
    }

    try {
      const issue = await api.restore()

      if (issue === 'unreachable') {
        // Serveur injoignable : on ne déconnecte SURTOUT pas. C'est le cas du
        // PWA rouvert sans réseau — toutes les données sont dans IndexedDB et
        // doivent rester accessibles.
        const cache = lireCache()
        set({ user: cache?.user ?? null, stores: cache?.stores ?? [], loading: false })
        return
      }

      if (issue === 'refused') {
        // Refus FRANC du serveur (session expirée ou révoquée) : le cache
        // n'a plus lieu d'être, il faut se reconnecter.
        ecrireCache(null)
        set({ user: null, stores: [], loading: false })
        return
      }
      const me = await api.get<AuthenticatedUser & { stores: StoreMembership[] }>('/auth/me')
      const user = {
        id: me.id,
        email: me.email,
        firstName: me.firstName,
        lastName: me.lastName,
        phone: me.phone ?? null,
        avatarUrl: me.avatarUrl ?? null,
        isPlatformAdmin: me.isPlatformAdmin,
      }
      ecrireCache({ user, stores: me.stores })
      set({ user, stores: me.stores, loading: false })
    } catch {
      // ⚠️ Panne RÉSEAU et non refus du serveur : on ne déconnecte pas. Le
      // cache prend le relais, sinon une simple coupure de WiFi effacerait la
      // session d'un utilisateur parfaitement légitime.
      const cache = lireCache()
      set({ user: cache?.user ?? null, stores: cache?.stores ?? [], loading: false })
    }
  },

  storeBySlug(slug) {
    return get().stores.find((s) => s.slug === slug)
  },

  rememberStore(slug) {
    localStorage.setItem(LAST_STORE_KEY, slug)
  },

  lastStore() {
    return localStorage.getItem(LAST_STORE_KEY)
  },
}))

// Le client HTTP ne connaît pas le store : il le prévient par ce rappel quand
// le rafraîchissement échoue définitivement.
setSessionLostHandler(() => {
  // Appelé sur un 401 franc, jamais sur une panne réseau (hors ligne, `fetch`
  // rejette avant qu'il y ait un statut). Le cache de reprise doit donc
  // disparaître avec la session.
  ecrireCache(null)
  useSessionStore.setState({ user: null, stores: [], loading: false })
})
